<template>
  <div>
    <div class="align-items-center justify-content-center d-md-flex m-5">
      <b-card style="border-radius: 20px; border: 1px solid lightgrey" class="px-1 m-0">

        <div class="mt-1 text-center">
          <b-link :to="{ name: 'Login' }">
            <img :src="appLogoImage" img-fluid width="200" />
          </b-link>
        </div>

        <b-card-title title-tag="h2" class="font-weight-bolder mb-50 mt-2 text-center">
          Welcome to Signal Sure Agent AI
        </b-card-title>

        <validation-observer ref="loginFormValidation">
          <b-form class="auth-login-form mt-2" @submit.prevent>

            <!-- Full Name -->
            <b-form-group label="Full Name">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="fullname" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Username -->
            <b-form-group label="Username">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="username" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Email -->
            <b-form-group label="Business Email">
              <validation-provider rules="required|email" v-slot="{ errors }">
                <b-form-input v-model="email" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Password -->
            <b-form-group label="Password">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-input-group>
                  <b-form-input
                    v-model="password"
                    :type="passwordFieldType"
                    :state="errors.length ? false : null"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- License Type -->
            <b-form-group label="License Type">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-radio-group v-model="license_type">
                  <b-form-radio value="individual">Individual Producer</b-form-radio>
                  <b-form-radio value="agency">Agency</b-form-radio>
                </b-form-radio-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Agency Name -->
            <b-form-group v-if="license_type === 'agency'" label="Agency Name">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="agency_name" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Business Phone -->
            <b-form-group label="Business Phone">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="business_phone" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- NPN -->
            <b-form-group label="NPN Number (Optional)">
              <b-form-input v-model="npn_number" />
            </b-form-group>

            <!-- Licensed States -->
            <b-form-group label="Licensed States">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-select v-model="licensed_states" :options="states" multiple />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Targeting Mode -->
            <b-form-group label="Primary Targeting Mode">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-select
                  v-model="primary_targeting_mode"
                  :options="[
                    { value: 'state', text: 'State Based' },
                    { value: 'zip', text: 'ZIP Code Based' }
                  ]"
                />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Target States -->
            <b-form-group v-if="primary_targeting_mode === 'state'" label="Target States">
              <b-form-select v-model="target_states" :options="states" multiple />
            </b-form-group>

            <!-- Target ZIP -->
            <b-form-group v-if="primary_targeting_mode === 'zip'" label="Target ZIP Codes">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="target_zip" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Products -->
            <b-form-group label="Products Offered">
              <b-form-checkbox-group v-model="products" class="demo-inline-spacing">
                <b-form-checkbox value="health">Health</b-form-checkbox>
                <b-form-checkbox value="life">Life</b-form-checkbox>
                <b-form-checkbox value="auto">Auto</b-form-checkbox>
                <b-form-checkbox value="home">Home</b-form-checkbox>
                <b-form-checkbox value="insurance">Insurance</b-form-checkbox>
                <b-form-checkbox value="mortgage">Mortgage</b-form-checkbox>
                <b-form-checkbox value="solar">Solar</b-form-checkbox>
                <b-form-checkbox value="legal">Legal</b-form-checkbox>
              </b-form-checkbox-group>
            </b-form-group>

            <!-- Compliance -->
            <b-form-group>
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-checkbox v-model="compliance_attested">
                  I confirm all compliance & licensing information is correct
                </b-form-checkbox>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-button block variant="primary" class="mt-3" @click="validateForm">
              Sign Up
            </b-button>

          </b-form>
        </validation-observer>
      </b-card>
    </div>
  </div>
</template>

<script>
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import { $themeConfig } from "@themeConfig";
import { mapActions } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  mixins: [togglePasswordVisibility],

  data() {
    return {
      appLogoImage: "",
      password: "",
      fullname: "",
      email: "",
      username: "",
      agency_name: "",
      business_phone: "",
      npn_number: "",
      license_type: "",
      licensed_states: [],
      primary_targeting_mode: "",
      target_states: [],
      target_zip: "",
      products: [],
      services_enabled: [],
      compliance_attested: false,

      states: [
        { value: "AL", text: "Alabama" },
        { value: "AK", text: "Alaska" },
        { value: "AZ", text: "Arizona" },
        { value: "AR", text: "Arkansas" },
        { value: "CA", text: "California" },
        { value: "CO", text: "Colorado" },
        { value: "CT", text: "Connecticut" },
        { value: "DE", text: "Delaware" },
        { value: "FL", text: "Florida" },
        { value: "GA", text: "Georgia" },
        { value: "HI", text: "Hawaii" },
        { value: "ID", text: "Idaho" },
        { value: "IL", text: "Illinois" },
        { value: "IN", text: "Indiana" },
        { value: "IA", text: "Iowa" },
        { value: "KS", text: "Kansas" },
        { value: "KY", text: "Kentucky" },
        { value: "LA", text: "Louisiana" },
        { value: "ME", text: "Maine" },
        { value: "MD", text: "Maryland" },
        { value: "MA", text: "Massachusetts" },
        { value: "MI", text: "Michigan" },
        { value: "MN", text: "Minnesota" },
        { value: "MS", text: "Mississippi" },
        { value: "MO", text: "Missouri" },
        { value: "MT", text: "Montana" },
        { value: "NE", text: "Nebraska" },
        { value: "NV", text: "Nevada" },
        { value: "NH", text: "New Hampshire" },
        { value: "NJ", text: "New Jersey" },
        { value: "NM", text: "New Mexico" },
        { value: "NY", text: "New York" },
        { value: "NC", text: "North Carolina" },
        { value: "ND", text: "North Dakota" },
        { value: "OH", text: "Ohio" },
        { value: "OK", text: "Oklahoma" },
        { value: "OR", text: "Oregon" },
        { value: "PA", text: "Pennsylvania" },
        { value: "RI", text: "Rhode Island" },
        { value: "SC", text: "South Carolina" },
        { value: "SD", text: "South Dakota" },
        { value: "TN", text: "Tennessee" },
        { value: "TX", text: "Texas" },
        { value: "UT", text: "Utah" },
        { value: "VT", text: "Vermont" },
        { value: "VA", text: "Virginia" },
        { value: "WA", text: "Washington" },
        { value: "WV", text: "West Virginia" },
        { value: "WI", text: "Wisconsin" },
        { value: "WY", text: "Wyoming" },
        { value: "DC", text: "District of Columbia" },
      ],
    };
  },

  created() {
    this.appLogoImage = $themeConfig.app.appLogoImage;
  },

  methods: {
    ...mapActions({
      createSignup: "appData/createSignup",
    }),

    async validateForm() {
      const valid = await this.$refs.loginFormValidation.validate();
      if (valid) this.submit();
    },

    async submit() {
      const payload = {
        user: {
          username: this.username,
          password: this.password,
          is_agent: true,
        },
        agent_profile: {
          full_name: this.fullname,
          agency_name: this.license_type === "agency" ? this.agency_name : null,
          business_email: this.email,
          business_phone: this.business_phone,
          npn_number: this.npn_number,
          license_type: this.license_type,
          licensed_states: this.licensed_states,
          primary_targeting_mode: this.primary_targeting_mode,
          target_states:
            this.primary_targeting_mode === "state" ? this.target_states : [],
          target_zip:
            this.primary_targeting_mode === "zip" ? this.target_zip : null,
          products: this.products,
          services_enabled: this.services_enabled,
          compliance_attested: this.compliance_attested,
        },
      };

      const res = await this.createSignup(payload);

      if (res.status === 201) {
        this.$toast(
          {
            component: ToastificationContent,
            props: {
              title: "User Signup Successfully",
              variant: "success",
              icon: "BellIcon",
            },
          },
          { position: "top-right" }
        );
        this.$router.push({ name: "SignupConfirmation" });
      }
    },
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
